import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class CrmAuthCookieService {
  private readonly accessTokenCookie = 'wm_access_token';
  private readonly refreshTokenCookie = 'wm_refresh_token';

  private readonly accessTokenExpiryKey = 'wm_access_token_expiry';
  private readonly refreshTokenExpiryKey = 'wm_refresh_token_expiry';

  setAuthCookies(payload: {
    accessToken: string;
    refreshToken: string;
    accessTokenExpiresUtc: string;
    refreshTokenExpiresUtc: string;
  }): void {
    this.writeCookie(this.accessTokenCookie, payload.accessToken, payload.accessTokenExpiresUtc);
    this.writeCookie(this.refreshTokenCookie, payload.refreshToken, payload.refreshTokenExpiresUtc);

    // Persist expiry times in sessionStorage for token-expiry/idle services to read
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(this.accessTokenExpiryKey, payload.accessTokenExpiresUtc);
      sessionStorage.setItem(this.refreshTokenExpiryKey, payload.refreshTokenExpiresUtc);
    }
  }

  getAccessTokenExpiresAt(): number | null {
    if (typeof window === 'undefined') return null;
    const raw = sessionStorage.getItem(this.accessTokenExpiryKey);
    if (!raw) return null;
    const ts = Date.parse(raw);
    return Number.isFinite(ts) ? ts : null;
  }

  getRefreshTokenExpiresAt(): number | null {
    if (typeof window === 'undefined') return null;
    const raw = sessionStorage.getItem(this.refreshTokenExpiryKey);
    if (!raw) return null;
    const ts = Date.parse(raw);
    return Number.isFinite(ts) ? ts : null;
  }

  getAccessToken(): string {
    return this.readCookie(this.accessTokenCookie);
  }

  getRefreshToken(): string {
    return this.readCookie(this.refreshTokenCookie);
  }

  hasAuthCookies(): boolean {
    return Boolean(this.getAccessToken() && this.getRefreshToken());
  }

  clearAuthCookies(): void {
    this.deleteCookie(this.accessTokenCookie);
    this.deleteCookie(this.refreshTokenCookie);
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(this.accessTokenExpiryKey);
      sessionStorage.removeItem(this.refreshTokenExpiryKey);
    }
  }

  private writeCookie(name: string, value: string, expiresUtc: string): void {
    if (typeof document === 'undefined') {
      return;
    }

    const expiresAt = Date.parse(expiresUtc);
    const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
    const expiresAttribute = Number.isFinite(expiresAt)
      ? `; Expires=${new Date(expiresAt).toUTCString()}`
      : '';

    document.cookie = `${name}=${encodeURIComponent(value)}${expiresAttribute}; Path=/; SameSite=Lax${secure}`;
  }

  private readCookie(name: string): string {
    if (typeof document === 'undefined') {
      return '';
    }

    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  }

  private deleteCookie(name: string): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.cookie = `${name}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; SameSite=Lax`;
  }
}